import React, { useEffect, useState, useRef } from 'react';
/**
 * ErrorSpottingRenderer — Find and correct spelling errors in text.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 */

import { QuestionRendererProps } from './types';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, Search } from 'lucide-react';
export function ErrorSpottingRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const text: string = config.text || '';
  const errors: Array<{
    word: string;
    position: number;
    correction: string;
    rule?: string;
  }> = config.errors || [];
  const [corrections, setCorrections] = useState<Record<number, string>>({});
  const [activeInput, setActiveInput] = useState<number | null>(null);
  const prevValidateRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const words = text.split(/\s+/);
  useEffect(() => {
    setCorrections({});
    setActiveInput(null);
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  useEffect(() => {
    onReadyChange?.(Object.keys(corrections).length === errors.length);
  }, [corrections, errors.length, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onAnswer(JSON.stringify(corrections));
      setActiveInput(null);
    }
  }, [shouldValidate, corrections, onAnswer]);
  useEffect(() => {
    if (activeInput !== null && inputRef.current) inputRef.current.focus();
  }, [activeInput]);
  const handleWordClick = (index: number) => {
    if (shouldValidate) return;
    if (activeInput === index) {
      setActiveInput(null);
      return;
    }
    setActiveInput(index);
    if (corrections[index] === undefined) {
      setCorrections(prev => ({
        ...prev,
        [index]: words[index].replace(/[.,!?]$/, '')
      }));
    }
  };
  const handleRemoveCorrection = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (shouldValidate) return;
    const next = {
      ...corrections
    };
    delete next[index];
    setCorrections(next);
    if (activeInput === index) setActiveInput(null);
  };
  if (!text || errors.length === 0) {
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-7039">
        <p className="text-red-700 font-bold" data-id="element-7040">
          ⚠️ Configuratie ontbreekt (text/errors)
        </p>
      </div>;
  }
  const foundCount = Object.keys(corrections).length;
  return <div className="w-full max-w-3xl mx-auto flex flex-col gap-6" data-id="element-7041">
      {/* Status Header */}
      <div className="flex items-center justify-between bg-indigo-50 rounded-2xl p-4 border-2 border-indigo-100" data-id="element-7042">
        <div className="flex items-center gap-3 text-indigo-700 font-bold" data-id="element-7043">
          <Search className="w-5 h-5" data-id="element-7044" />
          <span data-id="element-7045">Zoek de fouten</span>
        </div>
        <div className="flex items-center gap-2" data-id="element-7046">
          <span className="text-sm font-medium text-indigo-600/80" data-id="element-7047">
            Gevonden:
          </span>
          <div className="bg-white px-3 py-1 rounded-lg font-bold text-indigo-700 shadow-sm border border-indigo-100" data-id="element-7048">
            {foundCount} / {errors.length}
          </div>
        </div>
      </div>

      {/* Interactive Text Area */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 md:p-10 border-2 border-slate-200 shadow-sm leading-[3.5] text-lg sm:text-xl" data-id="element-7049">
        {words.map((word, index) => {
        const hasCorrection = corrections[index] !== undefined;
        const isActive = activeInput === index;
        let isCorrect = false;
        let showValidation = false;
        if (shouldValidate && hasCorrection) {
          showValidation = true;
          const errorDef = errors.find(e => e.position === index);
          if (errorDef) {
            isCorrect = corrections[index].toLowerCase().trim() === errorDef.correction.toLowerCase().trim();
          }
        }
        const match = word.match(/^([^.,!?]*)([.,!?]*)$/);
        const cleanWord = match ? match[1] : word;
        const punctuation = match ? match[2] : '';
        return <span key={index} className="relative inline-block mx-1" data-id="element-7050">
              <button onClick={() => handleWordClick(index)} disabled={shouldValidate} className={`relative px-2 py-1 rounded-lg transition-all font-medium ${shouldValidate ? 'cursor-default' : 'cursor-pointer hover:bg-slate-100'} ${hasCorrection ? 'opacity-40 line-through text-slate-500' : 'text-slate-800'} ${isActive ? 'ring-2 ring-indigo-400 bg-indigo-50' : ''}`} data-id="element-7051">
                {cleanWord}
              </button>
              {punctuation && <span className="text-slate-800" data-id="element-7052">{punctuation}</span>}

              <AnimatePresence data-id="element-7053">
                {hasCorrection && <motion.div initial={{
              opacity: 0,
              y: 10,
              scale: 0.9
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.9
            }} className={`absolute -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap z-10 flex items-center gap-2 px-3 py-1.5 rounded-xl shadow-md border-2 ${showValidation ? isCorrect ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800' : isActive ? 'bg-indigo-500 border-indigo-600 text-white' : 'bg-white border-indigo-200 text-indigo-700'}`} data-id="element-7054">
                    {isActive && !shouldValidate ? <input ref={inputRef} type="text" value={corrections[index]} onChange={e => setCorrections(prev => ({
                ...prev,
                [index]: e.target.value
              }))} onKeyDown={e => e.key === 'Enter' && setActiveInput(null)} onBlur={() => setActiveInput(null)} className="w-24 bg-transparent border-none outline-none text-center font-bold placeholder-indigo-300" placeholder="Verbetering..." data-id="element-7055" /> : <span className="font-bold cursor-pointer" onClick={() => handleWordClick(index)} data-id="element-7056">
                        {corrections[index]}
                      </span>}
                    {!shouldValidate && !isActive && <button onClick={e => handleRemoveCorrection(index, e)} className="p-0.5 hover:bg-indigo-100 rounded-full transition-colors" aria-label="Verwijder verbetering" data-id="element-7057">
                        <XCircle className="w-4 h-4 opacity-60" data-id="element-7058" />
                      </button>}
                    {showValidation && (isCorrect ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-7059" /> : <XCircle className="w-4 h-4 text-red-500" data-id="element-7060" />)}
                  </motion.div>}
              </AnimatePresence>
            </span>;
      })}
      </div>

      {!shouldValidate && <p className="text-center text-slate-500 text-sm font-medium" data-id="element-7061">
          Tik op een woord om het te verbeteren
        </p>}
    </div>;
}